// src/components/DigitalCardView.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { 
  RotateCw, 
  Award, 
  Radio, 
  Tag, 
  AlertCircle,
  Loader2 
} from 'lucide-react';
import './DigitalCardView.css';

const API_BASE_URL = "https://api.lbnhyderabad.com" || 'https://api.lbnhyderabad.com';

export default function DigitalCardView() {
  const { code } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFlipped, setIsFlipped] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/admin/members/card/${code}`)
      .then((res) => res.json())
      .then((json) => {
        if (json.success) setMember(json.member);
      })
      .catch((err) => console.error('Card verification error:', err))
      .finally(() => setLoading(false));
  }, [code]);

  // Universal URL resolver for photo path
  const getPhotoUrl = (url) => {
    if (!url) return null;

    // Normalize Windows backslashes (\) to forward slashes (/)
    const normalized = url.replace(/\\/g, '/');

    // If already absolute (Cloudinary, AWS S3, or external URL)
    if (normalized.startsWith('http://') || normalized.startsWith('https://') || normalized.startsWith('data:')) {
      return normalized;
    }

    // Ensure single leading slash
    const cleanPath = normalized.startsWith('/') ? normalized : `/${normalized}`;
    return `${API_BASE_URL}${cleanPath}`;
  };

  if (loading) {
    return (
      <div className="digital-card-page-root">
        <div style={{ textAlign: 'center', color: '#febf2c' }}>
          <Loader2 size={36} className="spin-loader" style={{ margin: '0 auto 12px' }} />
          <p style={{ fontSize: '0.86rem', color: '#e2e8f0' }}>Validating Privilege Credentials...</p>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="digital-card-page-root">
        <div style={{ textAlign: 'center', maxWidth: 400, padding: 24, background: 'rgba(255,255,255,0.05)', borderRadius: 16 }}>
          <AlertCircle size={44} color="#ef4444" style={{ margin: '0 auto 12px' }} />
          <h2 style={{ fontFamily: 'Playfair Display, serif', color: '#ffffff', margin: '0 0 6px' }}>Invalid Privilege Card</h2>
          <p style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
            No accredited member profile could be located for privilege identifier <code>{code}</code>.
          </p>
        </div>
      </div>
    );
  }

  const privCode = member.privilege_code || code;
  const supplierOffersUrl = `${window.location.origin}/offers?ref=${privCode}`;
  const photoSrc = !imageError ? getPhotoUrl(member.profile_photo_url) : null;

  return (
    <div className="digital-card-page-root">
      <div className="digital-card-container">
        {/* Verification Status Pill */}
        <div className="digital-verification-badge">
          <span className="verification-pulse" />
          <span>Accredited Member ID</span>
        </div>

        {/* 3D Interactive Card Scene */}
        <div 
          className="card-3d-scene" 
          onClick={() => setIsFlipped(!isFlipped)} 
          title="Click to flip between front and back"
        >
          <div className={`card-3d-object ${isFlipped ? 'flipped' : ''}`}>
            {/* ================= CARD FRONT ================= */}
            <div className="card-face card-face-front">
              {/* Header */}
              <div className="card-head-row">
                <div>
                  <div className="card-brand-name">Privilege Club</div>
                  <div className="card-brand-sub">EXECUTIVE IDENTITY</div>
                </div>
                <div className="card-chip-tag">
                  <Radio size={12} />
                  <span>PREMIUM ACCESS</span>
                </div>
              </div>

              {/* Member Profile Middle */}
              <div className="card-mid-row">
                <div className="card-avatar-photo">
                  {photoSrc ? (
                    <img 
                      src={photoSrc} 
                      alt={member.full_name} 
                      crossOrigin="anonymous"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <div className="card-avatar-fallback">
                      {member.full_name ? member.full_name.charAt(0).toUpperCase() : 'M'}
                    </div>
                  )}
                </div>

                <div className="card-member-info">
                  <div className="member-card-title">{member.full_name}</div>
                  <div className="member-card-id">{member.membership_number}</div>
                  <div className="member-card-meta">District {member.district} • {member.club_name}</div>
                  <div className="member-card-meta">M: {member.mobile}</div>
                </div>
              </div>

              {/* Bottom Row */}
              <div className="card-foot-row">
                <div className="card-code-block">
                  <span className="card-code-title">Privilege Identifier</span>
                  <span className="card-code-val">{privCode}</span>
                </div>

                {/* Manappuram Endorsement Badge */}
                <div className="card-sponsor-badge">
                  <Award size={18} color="#d97706" />
                  <div>
                    <span className="sponsor-title">MANAPPURAM</span>
                    <span className="sponsor-sub">FINANCE LIMITED</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ================= CARD BACK ================= */}
            <div className="card-face card-face-back">
              <div className="card-back-title">
                Privilege Partner Network
              </div>

              <div className="card-back-content">
                <div className="qr-box-white">
                  <QRCodeSVG 
                    value={supplierOffersUrl}
                    size={76}
                    bgColor="#ffffff"
                    fgColor="#002244"
                    level="M"
                  />
                </div>

                <div className="card-back-desc">
                  <h4>Scan for Exclusive Deals</h4>
                  <p>
                    Point camera or tap NFC to access accredited suppliers, commercial discounts, and partner catalog offers.
                  </p>
                  <p style={{ color: '#febf2c', fontWeight: 800 }}>
                    Code: {privCode}
                  </p>
                </div>
              </div>

              <div className="card-back-legal">
                Non-transferable • Issued by Privilege Card Authority • Delivery in 1 Month
              </div>
            </div>
          </div>
        </div>

        {/* Flip Action Trigger Hint */}
        <button 
          type="button" 
          className="flip-hint-row" 
          onClick={() => setIsFlipped(!isFlipped)}
        >
          <RotateCw size={13} />
          <span>{isFlipped ? 'Tap to View Front Face' : 'Tap to Flip & View QR Back'}</span>
        </button>

        {/* Credential Details Glass Card */}
        <div className="digital-profile-card">
          <div className="profile-card-title">Accreditation Particulars</div>
          <div className="profile-details-grid">
            <div className="profile-detail-row">
              <span>Member Full Name</span>
              <strong>{member.full_name}</strong>
            </div>
            <div className="profile-detail-row">
              <span>Privilege Code</span>
              <strong style={{ color: '#febf2c', fontFamily: 'monospace' }}>{privCode}</strong>
            </div>
            <div className="profile-detail-row">
              <span>Membership Identifier</span>
              <strong>{member.membership_number}</strong>
            </div>
            <div className="profile-detail-row">
              <span>District Allotment</span>
              <strong>District {member.district}</strong>
            </div>
            <div className="profile-detail-row">
              <span>Registered Club</span>
              <strong>{member.club_name}</strong>
            </div>
            <div className="profile-detail-row">
              <span>Accreditation Status</span>
              <strong style={{ color: '#4ade80' }}>Active & Verified</strong>
            </div>
          </div>
        </div>

        {/* Direct Link to Offers */}
        <Link to={`/offers?ref=${privCode}`} className="btn-offers-cta">
          <Tag size={18} />
          <span>Browse Active Supplier Offers</span>
        </Link>
      </div>
    </div>
  );
}